import { Locale } from "@core/schema/Locale.js";
import { getCartAddressPath } from "@core/schema/paths/getCartAddressPath.js";
import { getCartPath } from "@core/schema/paths/getCartPath.js";
import { getCartPaymentDeliveryPath } from "@core/schema/paths/getCartPaymentDeliveryPath.js";

export enum CartStep {
	Overview = "Overview",
	Address = "Address",
	PaymentDelivery = "PaymentDelivery",
	ReadyToSend = "ReadyToSend",
}

export const cartStepArray = [
	CartStep.Overview,
	CartStep.Address,
	CartStep.PaymentDelivery,
	CartStep.ReadyToSend,
] as const;

export type StepIndex<Step extends CartStep, Arr = typeof cartStepArray> = keyof {
	[K in keyof Arr as Arr[K] extends Step ? K : never]: unknown;
};

export type NextStepIndex<
	Step extends CartStep,
	I extends PropertyKey = StepIndex<Step, [null, ...typeof cartStepArray]>,
> = I extends keyof typeof cartStepArray ? I : never;

type DeleteFirst<T extends readonly unknown[]> = T extends readonly [unknown, ...infer R] ? R : never;

export type PreviousStepIndex<
	Step extends CartStep,
	I extends PropertyKey = StepIndex<Step, DeleteFirst<typeof cartStepArray>>,
> = I extends keyof typeof cartStepArray ? I : never;

export function getNextStep(step: CartStep): CartStep {
	const nextStepIndex = cartStepArray.indexOf(step) + 1;
	if (nextStepIndex > 0 && cartStepArray[nextStepIndex] !== undefined) {
		return cartStepArray[nextStepIndex];
	}
	return step;
}

export function getPreviousStep(step: CartStep): CartStep {
	const previousStepIndex = cartStepArray.indexOf(step) - 1;
	if (previousStepIndex >= 0 && cartStepArray[previousStepIndex] !== undefined) {
		return cartStepArray[previousStepIndex];
	}
	return step;
}

export function getCartStepName(step: Exclude<CartStep, CartStep.ReadyToSend>, locale: Locale): string {
	return {
		[CartStep.Overview]: {
			[Locale.cs]: "Košík",
		},
		[CartStep.PaymentDelivery]: {
			[Locale.cs]: "Doprava a platba",
		},
		[CartStep.Address]: {
			[Locale.cs]: "Adresa",
		},
	}[step][locale];
}

export function getCartStepPath(step: CartStep, locale: Locale): string {
	switch (step) {
		case CartStep.Overview:
			return getCartPath(locale);
		case CartStep.Address:
			return getCartAddressPath(locale);
		case CartStep.PaymentDelivery:
		case CartStep.ReadyToSend:
			return getCartPaymentDeliveryPath(locale);
	}
}
